<template>
  <div class="linkedin-message-view">
    <div v-if="conversation" class="linkedin-message-view__header">
      <div class="linkedin-message-view__header-content">
        <div class="linkedin-message-view__participants">
          <h3
            v-if="!hasMultipleParticipants"
            class="linkedin-message-view__contact-name linkedin-message-view__contact-name--clickable"
            @click="handleContactNameClick"
            :title="displayContactId ? 'View LinkedIn profile' : 'No LinkedIn profile available'"
          >
            {{ displayContactName }}
          </h3>
          <div v-else class="linkedin-message-view__participants-list">
            <div
              v-for="participant in otherParticipants"
              :key="participant.id"
              class="linkedin-message-view__participant"
            >
              <img
                v-if="participant.avatar || participant.picture_url"
                :src="(participant.avatar || participant.picture_url) ?? undefined"
                class="linkedin-message-view__participant-avatar"
                :alt="participant.name || 'Participant'"
              />
              <span class="linkedin-message-view__participant-name">{{
                participant.name || "Unknown"
              }}</span>
            </div>
            <span v-if="participantCount > 3" class="linkedin-message-view__participant-more">
              +{{ participantCount - 3 }} more
            </span>
          </div>
        </div>
        <div class="linkedin-message-view__status-wrapper">
          <span v-if="conversation.isActive" class="linkedin-message-view__status">
            <span
              class="linkedin-message-view__status-dot linkedin-message-view__status-dot--active"
            ></span>
            Active now
          </span>
          <span v-else-if="conversation.lastSeen" class="linkedin-message-view__status">
            <span class="linkedin-message-view__status-dot"></span>
            {{ conversation.lastSeen }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="getStatusBanner && !isStatusGood"
      class="linkedin-message-view__status-banner"
      :class="`linkedin-message-view__status-banner--${getStatusBanner.type}`"
    >
      <Icon
        :icon="getStatusBanner.icon"
        :class="{
          'linkedin-message-view__status-banner-icon--spinning':
            getStatusBanner.type === 'connecting',
        }"
      />
      <span class="linkedin-message-view__status-banner-message">{{
        getStatusBanner.message
      }}</span>
      <button
        v-if="(isStatusWarning || isStatusError) && channel"
        class="linkedin-message-view__status-banner-action"
        @click="handleReconnect"
      >
        <Icon icon="mdi:link-variant" />
        Reconnect
      </button>
    </div>

    <div v-if="isLoading" class="linkedin-message-view__loading">
      <div class="linkedin-message-view__loading-content">
        <LoadingBars size="sm" />
      </div>
    </div>

    <div
      v-else
      ref="parentRef"
      class="linkedin-message-view__messages"
      :style="{ visibility: isScrollReady ? 'visible' : 'hidden' }"
      @scroll="handleScroll"
    >
      <template v-if="shouldShowMessages">
        <div v-if="messages.length === 0" class="linkedin-message-view__empty">
          <Icon icon="mdi:message-outline" class="linkedin-message-view__empty-icon" />
          <p>No messages yet</p>
        </div>
        <template v-else>
          <div v-if="isLoadingOlderMessages" class="linkedin-message-view__loading-older">
            <LoadingBars size="sm" />
          </div>
          <div
            v-else-if="!hasMoreMessages && messages.length > 0"
            class="linkedin-message-view__no-more"
          >
            <span>No more messages</span>
          </div>

          <div
            :style="{
              height: `${totalSize}px`,
              width: '100%',
              position: 'relative',
            }"
          >
            <div
              v-for="virtualRow in virtualRows"
              :key="messages[virtualRow.index]?.id || virtualRow.index"
              :ref="(el) => measureElement(el as HTMLElement | null, virtualRow.index)"
              :data-index="virtualRow.index"
              :style="{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }"
            >
              <div
                class="linkedin-message-view__message-wrapper"
                :class="{
                  'linkedin-message-view__message-wrapper--highlighted':
                    highlightedMessageId === messages[virtualRow.index]?.id,
                }"
              >
                <div
                  v-if="shouldShowDateSeparator(virtualRow.index)"
                  class="linkedin-message-view__date-separator"
                >
                  <span>{{ formatDateSeparator(messages[virtualRow.index].timestamp) }}</span>
                </div>
                <MessageItem
                  :message="messages[virtualRow.index]"
                  platform="linkedin"
                  :show-sender="true"
                  :show-read-receipts="true"
                  :participants="conversation?.participants"
                  @reply="$emit('reply', messages[virtualRow.index])"
                  @ai-reply="$emit('ai-reply', messages[virtualRow.index])"
                  @ai-followup="$emit('ai-followup', messages[virtualRow.index])"
                  @forward="$emit('forward', messages[virtualRow.index])"
                  @delete="$emit('delete-message', messages[virtualRow.index])"
                  @react="$emit('react', messages[virtualRow.index], $event)"
                  @remove-reaction="$emit('remove-reaction', messages[virtualRow.index])"
                />
              </div>
            </div>
          </div>
        </template>
      </template>

      <template v-else>
        <div class="linkedin-message-view__empty linkedin-message-view__empty--status">
          <div class="linkedin-message-view__empty-content">
            <template v-if="isStatusConnecting">
              <LoadingBars size="sm" />
              <h3 class="linkedin-message-view__empty-title">Connecting to account...</h3>
              <p class="linkedin-message-view__empty-message">
                Please wait while we connect to your account.
              </p>
            </template>
            <template v-else-if="isStatusWarning">
              <Icon
                icon="mdi:alert-circle"
                class="linkedin-message-view__empty-icon linkedin-message-view__empty-icon--warning"
              />
              <h3 class="linkedin-message-view__empty-title">Reconnection Required</h3>
              <p class="linkedin-message-view__empty-message">
                {{
                  channelStatus?.toUpperCase() === "CREDENTIALS"
                    ? "Your account credentials have expired. Please reconnect to continue using messaging features."
                    : "Account synchronization has stopped. Please reconnect to continue."
                }}
              </p>
              <button
                v-if="channel"
                class="linkedin-message-view__empty-action"
                @click="handleReconnect"
              >
                <Icon icon="mdi:link-variant" />
                Reconnect
              </button>
            </template>
            <template v-else-if="isStatusError">
              <Icon
                icon="mdi:close-circle"
                class="linkedin-message-view__empty-icon linkedin-message-view__empty-icon--error"
              />
              <h3 class="linkedin-message-view__empty-title">Account Disconnected</h3>
              <p class="linkedin-message-view__empty-message">
                Your account has been disconnected. Please reconnect to access your messages.
              </p>
              <button
                v-if="channel"
                class="linkedin-message-view__empty-action"
                @click="handleReconnect"
              >
                <Icon icon="mdi:link-variant" />
                Reconnect
              </button>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Channel, Conversation, Message } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { computed, nextTick, ref, watch } from "vue";
import { useVirtualizer } from "@tanstack/vue-virtual";
import MessageItem from "./MessageItem.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { useMessageScroll } from "@/composables/messages/useMessageScroll";

interface Props {
  conversation: Conversation | null;
  messages: Message[];
  channelStatus?: string;
  channel?: Channel | null;
  isLoading?: boolean;
  isLoadingOlderMessages?: boolean;
  hasMoreMessages?: boolean;
  highlightMessageId?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: true,
  highlightMessageId: null,
});

const emit = defineEmits<{
  reply: [message: Message];
  "ai-reply": [message: Message];
  "ai-followup": [message: Message];
  forward: [message: Message];
  "delete-message": [message: Message];
  react: [message: Message, reaction: string];
  "remove-reaction": [message: Message];
  "reconnect-requested": [channel: Channel];
  "view-contact-profile": [contactId: string];
  "load-older-messages": [];
}>();

const handleContactNameClick = () => {
  if (displayContactId.value) {
    emit("view-contact-profile", displayContactId.value);
  }
};

const parentRef = ref<HTMLElement | null>(null);

const isStatusGood = computed(() => {
  if (!props.channelStatus) return true;
  const status = props.channelStatus.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
});

const isStatusConnecting = computed(() => {
  return props.channelStatus?.toUpperCase() === "CONNECTING";
});

const isStatusWarning = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["CREDENTIALS", "STOPPED"].includes(status);
});

const isStatusError = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status);
});

const shouldShowMessages = computed(() => {
  return isStatusGood.value;
});

const getStatusBanner = computed(() => {
  if (isStatusConnecting.value) {
    return {
      message: "Connecting to account...",
      icon: "mdi:loading",
      type: "connecting",
    };
  }
  if (isStatusWarning.value) {
    return {
      message:
        props.channelStatus?.toUpperCase() === "CREDENTIALS"
          ? "Reconnection required - Your account credentials have expired."
          : "Reconnection required - Account synchronization has stopped.",
      icon: "mdi:alert-circle",
      type: "warning",
    };
  }
  if (isStatusError.value) {
    return {
      message: "Account disconnected - Please reconnect to access messages.",
      icon: "mdi:close-circle",
      type: "error",
    };
  }
  return null;
});

const handleReconnect = () => {
  if (props.channel) {
    emit("reconnect-requested", props.channel);
  }
};

const shouldShowDateSeparator = (index: number): boolean => {
  if (index === 0) return true;
  const message = props.messages[index];
  const prevMessage = props.messages[index - 1];
  if (!message || !prevMessage) return false;
  const currentDate = new Date(message.timestamp).toDateString();
  const previousDate = new Date(prevMessage.timestamp).toDateString();
  return currentDate !== previousDate;
};

const formatDateSeparator = (timestamp: string): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "TODAY";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "YESTERDAY";
  }
  return date
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();
};

const conversationKey = computed(() => props.conversation?.id || "default");

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: props.messages.length,
    getScrollElement: () => parentRef.value,
    estimateSize: () => 72,
    overscan: 8,
    getItemKey: (index: number) => {
      const msg = props.messages[index] as any;
      return msg?._stableKey || msg?.id || index;
    },
  }))
);

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

const measureElement = (el: HTMLElement | null, _index: number) => {
  if (el) {
    rowVirtualizer.value.measureElement(el);
  }
};

const { handleScroll, isScrollReady, highlightedMessageId } = useMessageScroll({
  parentRef,
  rowVirtualizer: rowVirtualizer as any,
  virtualRows,
  messages: computed(() => props.messages),
  conversationKey,
  isLoading: computed(() => props.isLoading),
  isLoadingOlderMessages: computed(() => props.isLoadingOlderMessages),
  hasMoreMessages: computed(() => props.hasMoreMessages),
  onLoadOlderMessages: () => emit("load-older-messages"),
  highlightMessageId: computed(() => props.highlightMessageId),
});

watch(parentRef, (el) => {
  if (el && props.messages.length > 0) {
    nextTick(() => {
      requestAnimationFrame(() => {
        rowVirtualizer.value.scrollToIndex(props.messages.length - 1, { align: "end" });
      });
    });
  }
});

const otherParticipants = computed(() => {
  if (!props.conversation?.participants) return [];
  return props.conversation.participants.filter((p) => !p.is_self).slice(0, 3);
});

const participantCount = computed(() => {
  if (!props.conversation?.participants) return 0;
  return props.conversation.participants.filter((p) => !p.is_self).length;
});

const hasMultipleParticipants = computed(() => {
  return participantCount.value > 1;
});

const displayContactName = computed(() => {
  if (!props.conversation?.participants) {
    return props.conversation?.contactName || "Unknown";
  }
  const otherParticipants = props.conversation.participants.filter((p) => !p.is_self);
  if (otherParticipants.length > 0 && otherParticipants[0].name) {
    return otherParticipants[0].name;
  }
  return props.conversation?.contactName || "Unknown";
});

const displayContactId = computed(() => {
  if (!props.conversation?.participants) {
    return props.conversation?.contactId;
  }
  const otherParticipants = props.conversation.participants.filter((p) => !p.is_self);
  if (otherParticipants.length > 0) {
    return otherParticipants[0].providerUserId || props.conversation.contactId;
  }
  return props.conversation?.contactId;
});
</script>

<style scoped>
.linkedin-message-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #ffffff;
  position: relative;
}

.linkedin-message-view__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 12px 8px;
  border-bottom: 0.0625rem solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.linkedin-message-view__header-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  flex-wrap: wrap;
}

.linkedin-message-view__contact-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  margin: 0;
}

.linkedin-message-view__contact-name--clickable {
  cursor: pointer;
}

.linkedin-message-view__participants {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.linkedin-message-view__participants-list {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.linkedin-message-view__participant {
  display: flex;
  align-items: center;
  gap: 8px;
}

.linkedin-message-view__participant-avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  object-fit: cover;
}

.linkedin-message-view__participant-name {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.linkedin-message-view__participant-more {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.linkedin-message-view__status-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.linkedin-message-view__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
}

.linkedin-message-view__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  flex-shrink: 0;
}

.linkedin-message-view__status-dot--active {
  background: #10b981;
}

.linkedin-message-view__status-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  border-bottom: 0.0625rem solid #e5e7eb;
  font-size: 14px;
  flex-shrink: 0;
}

.linkedin-message-view__status-banner--connecting {
  background: #fffbeb;
  color: #d97706;
}

.linkedin-message-view__status-banner--warning {
  background: #fffbeb;
  color: #d97706;
}

.linkedin-message-view__status-banner--error {
  background: #fef2f2;
  color: #dc2626;
}

.linkedin-message-view__status-banner-icon--spinning {
  animation: spin 1s linear infinite;
}

.linkedin-message-view__status-banner svg {
  font-size: 20px;
  flex-shrink: 0;
}

.linkedin-message-view__status-banner-message {
  flex: 1;
  font-weight: 500;
}

.linkedin-message-view__status-banner-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 0.0625rem solid currentColor;
  border-radius: 0.375rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
}

.linkedin-message-view__status-banner-action:hover {
  background: rgba(255, 255, 255, 0.8);
}

.linkedin-message-view__loading {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.linkedin-message-view__loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.linkedin-message-view__loading-spinner {
  font-size: 32px;
  color: #6b7280;
  animation: spin 1s linear infinite;
}

.linkedin-message-view__loading-text {
  font-size: 14px;
  color: #6b7280;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.linkedin-message-view__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 12px;
  background: #fff;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.linkedin-message-view__messages::-webkit-scrollbar {
  width: 5px;
}

.linkedin-message-view__messages::-webkit-scrollbar-track {
  background: transparent;
}

.linkedin-message-view__messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.linkedin-message-view__messages::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.linkedin-message-view__loading-older {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
  flex-shrink: 0;
}

.linkedin-message-view__loading-older-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.linkedin-message-view__no-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
  flex-shrink: 0;
}

.linkedin-message-view__message-wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.linkedin-message-view__message-wrapper--highlighted {
  background-color: rgba(0, 119, 181, 0.12);
}

.linkedin-message-view__date-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 8px 0;
  position: relative;
}

.linkedin-message-view__date-separator::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 0.0625rem;
  background: #e5e7eb;
}

.linkedin-message-view__date-separator span {
  background: #fff;
  padding: 0 12px;
  font-size: 12px;
  color: #6b7280;
  position: relative;
  z-index: 1;
}

.linkedin-message-view__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 14px;
  padding: 32px;
}

.linkedin-message-view__empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.linkedin-message-view__empty--status {
  min-height: 18.75rem;
}

.linkedin-message-view__empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 20rem;
}

.linkedin-message-view__empty-icon--spinning {
  animation: spin 1s linear infinite;
  color: #f59e0b;
}

.linkedin-message-view__empty-icon--warning {
  color: #f59e0b;
}

.linkedin-message-view__empty-icon--error {
  color: #ef4444;
}

.linkedin-message-view__empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.linkedin-message-view__empty-message {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.linkedin-message-view__empty-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #0077b5;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.linkedin-message-view__empty-action:hover {
  background: #006399;
}
</style>
